import { useMemo, useState } from 'react';

import type { Artifact, PackageVersion } from '@/api/configuration';
import { useArtifactVersions, useDownloadUrl } from '@/hooks/use-artifact-download';
import { useArtifacts } from '@/hooks/use-configuration';

import { AssetSelector } from '@/components/deploy/asset-selector';

// --- Semver comparison utility ---

/**
 * Compares two version strings using semver-style ordering (descending: newest first).
 * Handles versions like "1.2.3", "1.2.3-SNAPSHOT", "1.2.3.4" gracefully.
 */
function compareSemverDesc(a: string, b: string): number {
  const partsA = a.split(/[.\-]/).map((p) => {
    const n = parseInt(p, 10);
    return isNaN(n) ? p : n;
  });
  const partsB = b.split(/[.\-]/).map((p) => {
    const n = parseInt(p, 10);
    return isNaN(n) ? p : n;
  });
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const pa = partsA[i];
    const pb = partsB[i];
    if (pa === undefined && pb !== undefined) return 1; // shorter = older
    if (pa !== undefined && pb === undefined) return -1;
    if (typeof pa === 'number' && typeof pb === 'number') {
      if (pa !== pb) return pb - pa; // descending
    } else {
      const sa = String(pa);
      const sb = String(pb);
      if (sa !== sb) return sb.localeCompare(sa); // descending
    }
  }
  return 0;
}

function sortVersionsDesc(versions: PackageVersion[]): PackageVersion[] {
  return [...versions].sort((a, b) => compareSemverDesc(a.version, b.version));
}

// --- Shared UI helpers ---

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-6 text-center">
      <p className="text-sm text-content-tertiary">{message}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="animate-pulse space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 rounded-lg bg-gray-200 dark:bg-gray-700" />
      ))}
    </div>
  );
}

// --- Accordion Item: single artifact with expandable version list ---

function ArtifactAccordionItem({
  artifact,
  projectKey,
  isExpanded,
  onToggle,
}: {
  artifact: Artifact;
  projectKey: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  const { data: versions, isLoading, isError, error } = useArtifactVersions(
    projectKey,
    isExpanded ? artifact.id : null
  );

  const sortedVersions = useMemo<PackageVersion[]>(
    () => versions ? sortVersionsDesc(versions) : [],
    [versions]
  );

  const downloadMutation = useDownloadUrl();
  const isPypi = artifact.downloadType === 'pypi';

  const handleVersionSelect = (version: string | null) => {
    setSelectedVersion(version);
    setSelectedAsset(null);
    downloadMutation.reset();
  };

  const handleDownload = (version: string, asset?: string) => {
    downloadMutation.mutate(
      {
        projectKey,
        artifactId: artifact.id,
        version,
        asset: asset || undefined,
      },
      {
        onSuccess: (response) => {
          window.open(response.url, '_blank');
        },
      }
    );
  };

  const notConfigured = !artifact.artifactRepository;

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      {/* Accordion header */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-surface-raised transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-inset"
      >
        <div className="flex items-center gap-3 min-w-0">
          <svg
            className={`h-4 w-4 flex-shrink-0 text-content-tertiary transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-content truncate">{artifact.name}</span>
          {artifact.downloadType && (
            <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-content-secondary">
              {artifact.downloadType}
            </span>
          )}
        </div>
        {notConfigured && (
          <span className="text-xs text-content-tertiary italic flex-shrink-0">Not configured</span>
        )}
      </button>

      {/* Accordion body */}
      {isExpanded && (
        <div className="border-t border-border px-4 py-3 bg-surface-raised/50">
          {notConfigured && (
            <p className="text-sm text-content-tertiary">
              No artifact repository configured. Set an artifact repository to enable version browsing.
            </p>
          )}

          {!notConfigured && isLoading && (
            <div className="space-y-1.5" aria-label="Loading versions">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-9 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse" />
              ))}
            </div>
          )}

          {!notConfigured && isError && (
            <p className="text-sm text-red-700 dark:text-red-300">
              Failed to load versions: {error?.message ?? 'Unknown error'}
            </p>
          )}

          {!notConfigured && !isLoading && !isError && versions && versions.length === 0 && (
            <p className="text-sm text-content-tertiary">No versions available.</p>
          )}

          {!notConfigured && !isLoading && !isError && versions && versions.length > 0 && (
            <div className="space-y-3">
              {/* Version list */}
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {sortedVersions.map((v) => {
                  const isSelected = selectedVersion === v.version;
                  return (
                    <div
                      key={v.version}
                      className={[
                        'flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors',
                        isSelected
                          ? 'border border-brand-600 bg-brand-50 dark:bg-brand-900/20'
                          : 'border border-transparent hover:bg-surface-raised',
                      ].join(' ')}
                    >
                      <button
                        type="button"
                        onClick={() => handleVersionSelect(isSelected ? null : v.version)}
                        className="flex-1 text-left focus:outline-none"
                      >
                        <span className={`font-medium ${isSelected ? 'text-brand-700 dark:text-brand-300' : 'text-content'}`}>
                          {v.version}
                        </span>
                        <span
                          className={[
                            'ml-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                            v.status === 'Published'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                          ].join(' ')}
                        >
                          {v.status}
                        </span>
                      </button>

                      {/* Direct download button for maven/npm */}
                      {!isPypi && (
                        <button
                          type="button"
                          onClick={() => handleDownload(v.version)}
                          disabled={downloadMutation.isPending}
                          className="ml-2 flex-shrink-0 rounded-md bg-brand-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                        >
                          {downloadMutation.isPending && downloadMutation.variables?.version === v.version
                            ? 'Loading...'
                            : 'Download'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* PyPI: show asset selector when a version is selected */}
              {isPypi && selectedVersion && (
                <div className="border-t border-border pt-3">
                  <AssetSelector
                    projectKey={projectKey}
                    artifactId={artifact.id}
                    version={selectedVersion}
                    selectedAsset={selectedAsset}
                    onAssetSelect={setSelectedAsset}
                  />
                  <div className="mt-2">
                    <button
                      type="button"
                      disabled={!selectedAsset || downloadMutation.isPending}
                      onClick={() => handleDownload(selectedVersion, selectedAsset!)}
                      className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                    >
                      {downloadMutation.isPending ? 'Loading...' : 'Download'}
                    </button>
                  </div>
                </div>
              )}

              {/* Download error */}
              {downloadMutation.isError && (
                <p className="text-xs text-red-700 dark:text-red-300">
                  Download failed: {downloadMutation.error?.message ?? 'Unknown error'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Download Tab ---

export function DownloadTab({ projectKey }: { projectKey: string }) {
  const { data: artifacts, isLoading, isError, error } = useArtifacts(projectKey);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) return <LoadingState />;

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
        <p className="text-sm text-red-700 dark:text-red-300">
          Failed to load artifacts: {error?.message ?? 'Unknown error'}
        </p>
      </div>
    );
  }

  // Only show artifacts that have a downloadType configured, sorted alphabetically
  const downloadableArtifacts = (artifacts?.filter((a) => !!a.downloadType) ?? [])
    .sort((a, b) => a.name.localeCompare(b.name));

  if (downloadableArtifacts.length === 0) {
    return <EmptyState message="No artifacts with a download type configured. Set a download type (maven, npm, pypi) on an artifact to enable version browsing." />;
  }

  return (
    <div className="space-y-2">
      {downloadableArtifacts.map((artifact) => (
        <ArtifactAccordionItem
          key={artifact.id}
          artifact={artifact}
          projectKey={projectKey}
          isExpanded={expandedId === artifact.id}
          onToggle={() => setExpandedId(expandedId === artifact.id ? null : artifact.id)}
        />
      ))}
    </div>
  );
}
